import React, { useState, useRef, useCallback } from 'react';
import api from '@/utils/api';
import { FaUpload, FaMapMarkerAlt, FaSearch, FaTrash, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import './Locations.scss';

type Tab = 'upload' | 'add' | 'manage';
type UploadMode = 'insert' | 'upsert';

interface UploadResult {
  dryRun?: boolean;
  processed?: number;
  parsed?: number;
  valid?: number;
  wouldInsert?: number;
  inserted?: number;
  skippedDuplicates?: number;
  duplicatesInFile?: number;
  invalidRows?: number;
  errors: Array<{ rowNum: number; error: string }>;
}

interface Location {
  id: string;
  countryCode: string;
  worldRegion: string | null;
  city: string | null;
  admin1: string | null;
  postalCode: string | null;
  lat: number;
  lon: number;
  decommissioned: boolean;
}

const EMPTY_FORM = {
  countryCode: '',
  city: '',
  admin1: '',
  postalCode: '',
  zipCodeType: '',
  locationType: '',
  worldRegion: '',
  locationText: '',
  lat: '',
  lon: '',
};

const AdminLocationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('upload');

  // ── CSV Upload state ──────────────────────────────────────────────────────
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<UploadMode>('insert');
  const [dryRun, setDryRun] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Single add state ──────────────────────────────────────────────────────
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // ── Manage state ──────────────────────────────────────────────────────────
  const [searchQ, setSearchQ] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ── CSV drop/select ───────────────────────────────────────────────────────
  const handleFileSelect = (selected: File | null) => {
    setFile(selected);
    setUploadResult(null);
    setUploadError('');
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setUploadResult(null);
    setUploadError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    formData.append('dryRun', String(dryRun));

    try {
      const { data } = await api.post('/admin/location/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(data);
    } catch (err: any) {
      setUploadError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadErrorCsv = () => {
    if (!uploadResult?.errors?.length) return;
    const lines = ['rowNum,error', ...uploadResult.errors.map((e) => `${e.rowNum},"${e.error}"`)];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'location-upload-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Single add ────────────────────────────────────────────────────────────
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveSuccess('');
    setSaveError('');
    try {
      await api.post('/admin/location', form);
      setSaveSuccess('Location saved successfully.');
      setForm(EMPTY_FORM);
    } catch (err: any) {
      setSaveError(err.response?.data?.error || 'Failed to save location.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Manage ────────────────────────────────────────────────────────────────
  const handleSearch = async (p = 1) => {
    setIsSearching(true);
    setSearchError('');
    try {
      const { data } = await api.get('/admin/location', {
        params: { q: searchQ, country: searchCountry, page: p, limit: 50 },
      });
      setLocations(data.locations);
      setTotal(data.total);
      setPage(p);
    } catch (err: any) {
      setSearchError(err.response?.data?.error || 'Search failed.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleDecommission = async (id: string) => {
    if (!confirm('Decommission this location? It will be hidden from users but not deleted.')) return;
    setDeletingId(id);
    try {
      await api.delete(`/admin/location/${id}`);
      setLocations((prev) => prev.filter((l) => l.id !== id));
      setTotal((t) => t - 1);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to decommission location.');
    } finally {
      setDeletingId(null);
    }
  };

  // ── Render tabs ───────────────────────────────────────────────────────────
  const renderUploadTab = () => (
    <div className="loc-section">
      <p className="loc-section-desc">
        Upload a CSV file to bulk-import locations. Required columns: <code>countryCode</code>, <code>lat</code>, <code>lon</code>.
        Optional: <code>city</code>, <code>admin1</code>, <code>postalCode</code>, <code>zipCodeType</code>, <code>locationType</code>, <code>worldRegion</code>, <code>locationText</code>.
        Column names are normalised — <code>State</code>, <code>Province</code>, <code>StatePro</code> all map to <code>admin1</code>.
        Max <strong>10,000 rows</strong> and <strong>5 MB</strong> per upload.
      </p>

      {/* Drop zone */}
      <div
        className={`loc-dropzone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv,application/vnd.ms-excel"
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
        />
        <FaUpload className="dropzone-icon" />
        {file ? (
          <div>
            <p className="dropzone-filename">{file.name}</p>
            <p className="dropzone-size">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
        ) : (
          <p>Drag & drop a CSV file here, or <span className="dropzone-link">click to browse</span></p>
        )}
      </div>

      {/* Options */}
      <div className="loc-options">
        <div className="loc-option-group">
          <label className="loc-option-label">Import mode</label>
          <div className="loc-radio-group">
            <label className={`loc-radio ${mode === 'insert' ? 'active' : ''}`}>
              <input type="radio" value="insert" checked={mode === 'insert'} onChange={() => setMode('insert')} />
              <span><strong>Insert only</strong> — skip rows that already exist (by country + postal code)</span>
            </label>
            <label className={`loc-radio ${mode === 'upsert' ? 'active' : ''}`}>
              <input type="radio" value="upsert" checked={mode === 'upsert'} onChange={() => setMode('upsert')} />
              <span><strong>Upsert</strong> — update existing rows with new data</span>
            </label>
          </div>
        </div>

        <label className="loc-checkbox">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          <span><strong>Dry run</strong> — parse and validate without writing to the database</span>
        </label>
      </div>

      {uploadError && <div className="loc-error">{uploadError}</div>}

      <button
        className="loc-btn loc-btn-primary"
        onClick={handleUpload}
        disabled={!file || isUploading}
      >
        {isUploading ? 'Processing…' : dryRun ? 'Run Preview' : 'Upload'}
      </button>

      {/* Results */}
      {uploadResult && (
        <div className={`loc-result ${uploadResult.dryRun ? 'dry-run' : 'success'}`}>
          <div className="result-header">
            <FaCheckCircle className="result-icon" />
            <h3>{uploadResult.dryRun ? 'Dry Run Preview' : 'Upload Complete'}</h3>
          </div>
          <div className="result-stats">
            {uploadResult.dryRun ? (
              <>
                <div className="stat"><span>Rows in CSV</span><strong>{uploadResult.parsed}</strong></div>
                <div className="stat"><span>Valid rows</span><strong>{uploadResult.valid}</strong></div>
                <div className="stat"><span>Duplicates in file</span><strong>{uploadResult.duplicatesInFile}</strong></div>
                <div className="stat"><span>Would insert</span><strong>{uploadResult.wouldInsert}</strong></div>
                <div className="stat error"><span>Invalid rows</span><strong>{uploadResult.invalidRows}</strong></div>
              </>
            ) : (
              <>
                <div className="stat"><span>Rows processed</span><strong>{uploadResult.processed}</strong></div>
                <div className="stat"><span>Inserted / updated</span><strong>{uploadResult.inserted}</strong></div>
                <div className="stat"><span>Skipped (DB duplicates)</span><strong>{uploadResult.skippedDuplicates}</strong></div>
                <div className="stat"><span>Duplicates in file</span><strong>{uploadResult.duplicatesInFile}</strong></div>
                <div className="stat error"><span>Invalid rows</span><strong>{uploadResult.invalidRows}</strong></div>
              </>
            )}
          </div>
          {uploadResult.errors.length > 0 && (
            <div className="result-errors">
              <div className="result-errors-header">
                <FaExclamationTriangle />
                <span>Row errors {uploadResult.errors.length >= 50 ? '(showing first 50)' : ''}</span>
                <button className="loc-btn loc-btn-sm" onClick={downloadErrorCsv}>Download CSV</button>
              </div>
              <table className="errors-table">
                <thead><tr><th>Row</th><th>Error</th></tr></thead>
                <tbody>
                  {uploadResult.errors.map((e, i) => (
                    <tr key={i}><td>{e.rowNum}</td><td>{e.error}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderAddTab = () => (
    <div className="loc-section">
      <p className="loc-section-desc">Add a single location. Fields marked * are required.</p>
      {saveSuccess && <div className="loc-success">{saveSuccess}</div>}
      {saveError && <div className="loc-error">{saveError}</div>}
      <form className="loc-form" onSubmit={handleSave}>
        <div className="loc-form-row">
          <div className="loc-field">
            <label>Country Code *</label>
            <input
              type="text" maxLength={2} placeholder="US" required
              value={form.countryCode}
              onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })}
            />
            <span className="field-hint">ISO 2-letter code</span>
          </div>
          <div className="loc-field">
            <label>Latitude *</label>
            <input
              type="number" step="any" placeholder="40.7128" required
              value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })}
            />
          </div>
          <div className="loc-field">
            <label>Longitude *</label>
            <input
              type="number" step="any" placeholder="-74.0060" required
              value={form.lon}
              onChange={(e) => setForm({ ...form, lon: e.target.value })}
            />
          </div>
        </div>
        <div className="loc-form-row">
          <div className="loc-field">
            <label>City</label>
            <input type="text" placeholder="New York"
              value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="loc-field">
            <label>State / Province</label>
            <input type="text" placeholder="NY"
              value={form.admin1} onChange={(e) => setForm({ ...form, admin1: e.target.value })} />
          </div>
          <div className="loc-field">
            <label>Postal Code</label>
            <input type="text" placeholder="10001"
              value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
          </div>
        </div>
        <div className="loc-form-row">
          <div className="loc-field">
            <label>World Region</label>
            <input type="text" placeholder="NA"
              value={form.worldRegion} onChange={(e) => setForm({ ...form, worldRegion: e.target.value })} />
          </div>
          <div className="loc-field">
            <label>Location Type</label>
            <input type="text" placeholder="ZIP"
              value={form.locationType} onChange={(e) => setForm({ ...form, locationType: e.target.value })} />
          </div>
          <div className="loc-field">
            <label>Zip Code Type</label>
            <input type="text" placeholder="STANDARD"
              value={form.zipCodeType} onChange={(e) => setForm({ ...form, zipCodeType: e.target.value })} />
          </div>
        </div>
        <div className="loc-form-row">
          <div className="loc-field loc-field--full">
            <label>Location Text</label>
            <input type="text" placeholder="New York City, NY, US"
              value={form.locationText} onChange={(e) => setForm({ ...form, locationText: e.target.value })} />
            <span className="field-hint">Human-readable label used in search</span>
          </div>
        </div>
        <button type="submit" className="loc-btn loc-btn-primary" disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Add Location'}
        </button>
      </form>
    </div>
  );

  const renderManageTab = () => (
    <div className="loc-section">
      <div className="loc-search-bar">
        <input
          type="text" placeholder="Search city, postal code, region…"
          value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
        />
        <input
          type="text" maxLength={2} placeholder="CC" title="Country code filter"
          value={searchCountry} onChange={(e) => setSearchCountry(e.target.value.toUpperCase())}
          className="loc-search-cc"
        />
        <button className="loc-btn loc-btn-primary" onClick={() => handleSearch(1)} disabled={isSearching}>
          <FaSearch /> {isSearching ? 'Searching…' : 'Search'}
        </button>
      </div>
      {searchError && <div className="loc-error">{searchError}</div>}
      {locations.length > 0 && (
        <>
          <p className="loc-count">{total.toLocaleString()} result{total !== 1 ? 's' : ''}</p>
          <div className="loc-table-wrap">
            <table className="loc-table">
              <thead>
                <tr>
                  <th>CC</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Postal</th>
                  <th>Lat</th>
                  <th>Lon</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {locations.map((loc) => (
                  <tr key={loc.id} className={loc.decommissioned ? 'decommissioned' : ''}>
                    <td><strong>{loc.countryCode}</strong></td>
                    <td>{loc.city || '—'}</td>
                    <td>{loc.admin1 || '—'}</td>
                    <td>{loc.postalCode || '—'}</td>
                    <td>{loc.lat.toFixed(4)}</td>
                    <td>{loc.lon.toFixed(4)}</td>
                    <td>
                      <span className={`loc-status ${loc.decommissioned ? 'inactive' : 'active'}`}>
                        {loc.decommissioned ? 'Decommissioned' : 'Active'}
                      </span>
                    </td>
                    <td>
                      {!loc.decommissioned && (
                        <button
                          className="loc-btn loc-btn-danger loc-btn-sm"
                          onClick={() => handleDecommission(loc.id)}
                          disabled={deletingId === loc.id}
                          title="Decommission"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 50 && (
            <div className="loc-pagination">
              <button className="loc-btn loc-btn-sm" onClick={() => handleSearch(page - 1)} disabled={page <= 1}>Previous</button>
              <span>Page {page} of {Math.ceil(total / 50)}</span>
              <button className="loc-btn loc-btn-sm" onClick={() => handleSearch(page + 1)} disabled={page >= Math.ceil(total / 50)}>Next</button>
            </div>
          )}
        </>
      )}
      {!isSearching && locations.length === 0 && total === 0 && searchQ === '' && searchCountry === '' && (
        <p className="loc-empty">Enter a search term or country code to browse locations.</p>
      )}
    </div>
  );

  return (
    <div className="admin-locations-page">
      <div className="admin-locations-container">
        <div className="loc-header">
          <FaMapMarkerAlt className="loc-header-icon" />
          <div>
            <h1>Location Management</h1>
            <p>Bulk import, add, and manage location data used for discovery and profile search.</p>
          </div>
        </div>

        <div className="loc-tabs">
          {([['upload', 'Bulk CSV Upload'], ['add', 'Add Single'], ['manage', 'Browse & Manage']] as const).map(([id, label]) => (
            <button
              key={id}
              className={`loc-tab ${activeTab === id ? 'active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'upload' && renderUploadTab()}
        {activeTab === 'add' && renderAddTab()}
        {activeTab === 'manage' && renderManageTab()}
      </div>
    </div>
  );
};

export default AdminLocationsPage;
